'use client';

import { useEffect, useId, useMemo, useReducer, useState } from 'react';
import { BlockRoot, useCart } from '@akademi/site-builder-runtime';
import { rangeLook, type DoorCatalogue, type DoorRange } from '../data/doorCatalogue';
import type { DoorOrderSpec, DoorPricing, DoorQuote } from '../data/doorPricing';
import { formatGbp } from '../data/doorPricing';
import { useSpatialPorts } from '../data/ports';
import { useDoorCatalogue } from '../data/useDoorCatalogue';
import { doorSvg } from '../drawing/door';
import { DOOR_BUILDER_CSS } from '../doors/doorBuilderCss';
import { DoorViewer } from '../doors/DoorViewer';
import { DrillEditor } from '../doors/DrillEditor';
import { PIECE_LABEL, sizeIssues, type HandleProduct, type PieceKind } from '../doors/drilling';
import { PiecesTable } from '../doors/PiecesTable';
import { ColourPicker, RangePicker } from '../doors/RangePicker';
import { doorOrderReducer, hasRanges, initialOrder, rangesFor, type DoorsFor } from '../doors/orderState';
import { headingLevel, items, SkLink, text, type Localized } from '../shared/primitives';
import { NotOpen } from '../shared/NotOpen';
import { SkBaseStyles, SkStyles } from '../styles/SkStyles';

/**
 * The replacement-door order builder (site mockup `doors.html`): choose a range
 * and colour, list every piece with its size, hinge side and quantity, check
 * and edit each piece's hinge and handle drilling against a dimensioned back
 * view, and see the order priced as it changes.
 *
 * <h3>Data</h3>
 * Everything comes through the Spatial ports (`data/ports.tsx`): the catalogue,
 * the server's price, the basket and saved designs. Without a door catalogue
 * and price the block shows that ordering is not open; with no basket, the order
 * can only be saved to the account, and only when the account keeps doors.
 *
 * <h3>SEO</h3>
 * The page heading, intro, chosen range and the standard-size list render on
 * the server. A landing page per range or colour presets `rangeId`/`colourId`.
 * Structured data (ProductGroup with an AggregateOffer) is emitted when the
 * page knows its own https address.
 */
export interface PopularSize {
  label?: Localized;
  h: number;
  w: number;
}

export interface SpatialKitchenDoorBuilderProps {
  title?: Localized;
  intro?: Localized;
  headingLevel?: 'h1' | 'h2';
  mode?: DoorsFor;
  /** Preset range and colour, for a range or colour landing page. */
  rangeId?: string;
  colourId?: string;
  showModeSwitch?: boolean;
  sampleHref?: string;
  measureHref?: string;
  myDesignsHref?: string;
  popularTitle?: Localized;
  popularIntro?: Localized;
  popularSizes?: PopularSize[];
  /** Absolute page URL, for structured data. */
  pageUrl?: string;
}

const DEFAULT_POPULAR: PopularSize[] = [
  { label: 'Base door', h: 715, w: 597 },
  { label: 'Base door', h: 715, w: 497 },
  { label: 'Base door', h: 715, w: 447 },
  { label: 'Base door', h: 715, w: 397 },
  { label: 'Base door', h: 715, w: 297 },
  { label: 'Wall door', h: 895, w: 497 },
  { label: 'Tall larder door', h: 1245, w: 597 },
  { label: 'Drawer front', h: 140, w: 597 },
];

/** The shopper's own handles, which set the default drilling. The shop does not supply handles yet. */
const HANDLES: Array<[HandleProduct, string]> = [
  ['bar', 'Bar (2 holes)'],
  ['knob', 'Knob (1 hole)'],
  ['cup', 'Cup (2 holes)'],
  ['none', 'No holes'],
];

const KIND_NAMES: Record<PieceKind, string> = { door: 'Doors', drawer: 'Drawer fronts', panel: 'End panels' };

/** "Doors up to 2150 × 997 mm · …": the largest standard piece of each kind, which caps a made-to-measure one. */
function sizeSummary(range: DoorRange): string {
  return (Object.keys(range.sizes) as PieceKind[])
    .map((kind) => {
      const s = range.sizes[kind]!;
      return `${KIND_NAMES[kind]} up to ${s.maxH} × ${s.maxW} mm`;
    })
    .join(' · ');
}

function jsonLd(data: unknown): string {
  return JSON.stringify(data).replace(/</g, '\\u003c');
}

function defaultTitle(props: SpatialKitchenDoorBuilderProps): string {
  return text(props.title, props.mode === 'wardrobe' ? 'Wardrobe doors, made to measure' : 'Replacement kitchen doors, made to measure');
}

export function SpatialKitchenDoorBuilder(props: SpatialKitchenDoorBuilderProps) {
  const { doorPricing } = useSpatialPorts();
  const doors = useDoorCatalogue();
  if (doors.status === 'loading') {
    const Heading = headingLevel(props.headingLevel, 'h1');
    return (
      <BlockRoot as="section" className="sk sk-db" aria-busy="true">
        <SkBaseStyles />
        <SkStyles id="door-builder" css={DOOR_BUILDER_CSS} />
        <div className="sk-db__wrap">
          <Heading className="sk-heading sk-db__title">{defaultTitle(props)}</Heading>
          <p className="sk-lead" role="status">
            Loading the door ranges…
          </p>
        </div>
      </BlockRoot>
    );
  }
  if (doors.status === 'error') {
    return (
      <NotOpen
        level={props.headingLevel}
        title={defaultTitle(props)}
        message={`${doors.message} Please reload the page in a moment. If it keeps happening, tell us your sizes and we will price them for you.`}
      />
    );
  }
  if (doors.status === 'none' || !doorPricing || !hasRanges(doors.catalogue, props.mode ?? 'kitchen')) {
    return (
      <NotOpen
        level={props.headingLevel}
        title={defaultTitle(props)}
        message="Ordering made-to-measure doors online is not open yet. Tell us your sizes and the style you want, and we will price them for you."
      />
    );
  }
  return <DoorBuilder {...props} catalogue={doors.catalogue} doorPricing={doorPricing} />;
}

function DoorBuilder(props: SpatialKitchenDoorBuilderProps & { catalogue: DoorCatalogue; doorPricing: DoorPricing }) {
  const id = useId();
  const { catalogue, doorPricing } = props;
  const { savedDesigns, doorBasket } = useSpatialPorts();
  const cart = useCart();
  const reducer = useMemo(() => doorOrderReducer(catalogue), [catalogue]);
  const [state, dispatch] = useReducer(reducer, undefined, () =>
    initialOrder(catalogue, { mode: props.mode, rangeId: props.rangeId, colourId: props.colourId }),
  );
  const [quote, setQuote] = useState<DoorQuote | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [status, setStatus] = useState<{ kind: 'ok' | 'error'; message: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [initialFamily, setInitialFamily] = useState<string | undefined>(undefined);

  // The URL can preset the builder (?range=, ?colour=, ?mode=wardrobe, ?family=). Read after
  // mount so the server render and the first client render match.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const family = params.get('family');
    if (params.get('mode') === 'wardrobe' || family === 'wardrobe' || window.location.hash === '#wardrobe') dispatch({ type: 'mode', mode: 'wardrobe' });
    const range = params.get('range');
    if (range) dispatch({ type: 'range', rangeId: range });
    const colour = params.get('colour');
    if (colour) dispatch({ type: 'colour', colourId: colour });
    if (family && family !== 'wardrobe') setInitialFamily(family);
  }, []);

  // The shop supplies no handles or hinges yet: the order is drilled for the shopper's own, so
  // `handle` and `softClose` go to the server as none. The handle choice above only sets the
  // default drilling of each piece.
  const paintColour = state.paintColour.trim();
  const spec: DoorOrderSpec = useMemo(
    () => ({
      rangeId: state.rangeId,
      colourId: state.colourId,
      ...(state.colourId === 'ral' ? { paintColour } : {}),
      handle: 'none',
      softClose: false,
      pieces: state.pieces,
    }),
    [state.rangeId, state.colourId, paintColour, state.pieces],
  );
  const paintNamed = state.colourId !== 'ral' || paintColour.length >= 2;
  const valid = paintNamed && state.pieces.every((p) => sizeIssues(p).length === 0);

  useEffect(() => {
    if (!valid) {
      setQuote(null);
      return;
    }
    let current = true;
    doorPricing
      .quote(spec)
      .then((q) => {
        if (current) {
          setQuote(q);
          setQuoteError(null);
        }
      })
      .catch((error: unknown) => {
        if (current) {
          setQuote(null);
          setQuoteError(error instanceof Error ? error.message : 'The price could not be worked out.');
        }
      });
    return () => {
      current = false;
    };
  }, [doorPricing, spec, valid]);

  const ranges = rangesFor(catalogue, state.mode);
  const range = catalogue.ranges().find((r) => r.id === state.rangeId) ?? ranges[0];
  if (!range) return null;
  const colour = catalogue.colours().find((c) => c.id === state.colourId);
  const colourHex = state.colourId === 'ral' ? '#9a7f6a' : (colour?.hex ?? '#8a9a82');
  const colourName = state.colourId === 'ral' ? (paintColour ? `Painted ${paintColour}` : 'Painted to order') : (colour?.name ?? '');
  const active = state.pieces[state.active] ?? state.pieces[0];
  const Heading = headingLevel(props.headingLevel, 'h1');
  const popular = items<PopularSize>(props.popularSizes).length ? items<PopularSize>(props.popularSizes) : DEFAULT_POPULAR;
  const myDesignsHref = props.myDesignsHref || '/my-designs';

  async function order() {
    if (!quote || !valid) return;
    setBusy(true);
    setStatus(null);
    try {
      if (doorBasket) {
        await doorBasket.add(spec);
        // The server added the lines; the header's basket learns of them by re-reading it.
        await cart?.refresh();
        setStatus({ kind: 'ok', message: 'Added to your basket.' });
      } else {
        await savedDesigns.save({
          kind: 'doors',
          title: `${range!.name} doors`,
          summary: `${range!.name} · ${colourName} · ${quote.pieces} piece${quote.pieces === 1 ? '' : 's'}`,
          href: `/doors?range=${encodeURIComponent(state.rangeId)}&colour=${encodeURIComponent(state.colourId)}`,
          spec,
          total: quote.total,
          thumbnailSvg: doorSvg(range!.style, colourHex, { look: rangeLook(range!), w: 300, h: 500, wood: colour?.wood, label: `${range!.name} door`, idPrefix: 'thumb' }),
        });
        setStatus({ kind: 'ok', message: 'Saved to My designs.' });
      }
    } catch (error) {
      setStatus({ kind: 'error', message: error instanceof Error ? error.message : 'That did not go through. Please try again.' });
    } finally {
      setBusy(false);
    }
  }

  const structured =
    props.pageUrl?.startsWith('https://')
      ? {
          '@context': 'https://schema.org',
          '@type': 'ProductGroup',
          name: `${range.name} made-to-measure ${state.mode === 'wardrobe' ? 'wardrobe' : 'kitchen'} door`,
          productGroupID: `door-${range.id}`,
          url: props.pageUrl,
          variesBy: ['https://schema.org/color', 'https://schema.org/size'],
        }
      : null;

  return (
    <BlockRoot as="section" className="sk sk-db" aria-labelledby={`${id}-title`}>
      <SkBaseStyles />
      <SkStyles id="door-builder" css={DOOR_BUILDER_CSS} />
      {structured && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd(structured) }} />}
      <div className="sk-db__wrap">
        <div className="sk-db__head">
          <div>
            <Heading id={`${id}-title`} className="sk-heading sk-db__title">
              {text(props.title, state.mode === 'wardrobe' ? 'Wardrobe doors, made to measure' : 'Replacement kitchen doors, made to measure')}
            </Heading>
            <p className="sk-lead">
              {text(props.intro, 'Pick a range and a colour, type in your sizes, and every door and drawer front is made to the millimetre.')}
            </p>
          </div>
          {props.showModeSwitch !== false && hasRanges(catalogue, 'kitchen') && hasRanges(catalogue, 'wardrobe') && (
            <div className="sk-seg" role="group" aria-label="Doors for">
              <button type="button" aria-pressed={state.mode === 'kitchen'} onClick={() => dispatch({ type: 'mode', mode: 'kitchen' })}>
                Kitchen doors
              </button>
              <button type="button" aria-pressed={state.mode === 'wardrobe'} onClick={() => dispatch({ type: 'mode', mode: 'wardrobe' })}>
                Wardrobe doors
              </button>
            </div>
          )}
        </div>

        <div className="sk-db__grid">
          <DoorViewer
            view={state.view}
            pieces={state.pieces}
            active={state.active}
            style={range.style}
            look={rangeLook(range)}
            colour={colourHex}
            wood={state.colourId !== 'ral' && Boolean(colour?.wood)}
            product={range.noHandle ? 'none' : state.handle}
            tag={`${range.name} · ${colourName}`}
            build={range.build}
            dispatch={dispatch}
          />

          <div>
            <div className="sk-db__panel">
              <div className="sk-db__step">
                <span className="sk-db__n" aria-hidden="true">1</span>
                <h2 className="sk-heading">Door range</h2>
                <span className="sk-db__aside">{`${ranges.length} ranges`}</span>
              </div>
              <RangePicker
                catalogue={catalogue}
                ranges={ranges}
                range={range}
                colourId={state.colourId}
                initialFamily={initialFamily}
                onPick={(rangeId) => dispatch({ type: 'range', rangeId })}
              />
            </div>

            <div className="sk-db__panel">
              <div className="sk-db__step">
                <span className="sk-db__n" aria-hidden="true">2</span>
                <h2 className="sk-heading">Colour</h2>
                {props.sampleHref && (
                  <SkLink href={props.sampleHref} className="sk-db__aside sk-link-arrow">
                    Order a free sample
                  </SkLink>
                )}
              </div>
              <ColourPicker
                catalogue={catalogue}
                range={range}
                colourId={state.colourId}
                onPick={(colourId) => dispatch({ type: 'colour', colourId })}
                paintColour={state.paintColour}
                onPaintColour={(value) => dispatch({ type: 'paintColour', value })}
              />
            </div>

            <div className="sk-db__panel" id="rows">
              <div className="sk-db__step">
                <span className="sk-db__n" aria-hidden="true">3</span>
                <h2 className="sk-heading">Your sizes</h2>
                {props.measureHref && (
                  <SkLink href={props.measureHref} className="sk-db__aside sk-link-arrow">
                    How to measure
                  </SkLink>
                )}
              </div>
              <p className="sk-db__hint">{`${sizeSummary(range)}. Each piece is cut down from the next standard size up, and priced as that size.`}</p>
              <PiecesTable pieces={state.pieces} active={state.active} quote={quote} dispatch={dispatch} />
              {active && (
                <DrillEditor
                  piece={active}
                  index={state.active}
                  total={state.pieces.length}
                  product={range.noHandle ? 'none' : state.handle}
                  dispatch={dispatch}
                />
              )}
            </div>

            <div className="sk-db__panel">
              <div className="sk-db__step">
                <span className="sk-db__n" aria-hidden="true">4</span>
                <h2 className="sk-heading">Drilling for your handles</h2>
              </div>
              <div className="sk-db__field" style={{ marginBottom: '.5rem' }}>
                <span className="sk-db__label" id={`${id}-handles`}>
                  Your handles
                </span>
                <div className="sk-seg" role="group" aria-labelledby={`${id}-handles`}>
                  {HANDLES.map(([handle, label]) => (
                    <button
                      key={handle}
                      type="button"
                      aria-pressed={state.handle === handle}
                      disabled={range.noHandle}
                      onClick={() => dispatch({ type: 'handleProduct', handle })}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <span className="sk-db__hint">
                  {range.noHandle
                    ? `${range.name} is made for a handle rail, so no handles are drilled.`
                    : 'We drill the holes where you set them on each piece. Handles and hinges are not included.'}
                </span>
              </div>
            </div>

            <div className="sk-db__summary" aria-live="polite">
              {quote ? (
                <>
                  <div className="sk-db__line">
                    <span>{`${quote.pieces} piece${quote.pieces === 1 ? '' : 's'}, made to measure`}</span>
                    <span>{formatGbp(quote.piecesTotal)}</span>
                  </div>
                  <div className="sk-db__line">
                    <span>Hinge &amp; handle drilling</span>
                    <span>{formatGbp(quote.extrasTotal)}</span>
                  </div>
                  <div className="sk-db__line">
                    <span>Delivery</span>
                    <span>Priced at checkout</span>
                  </div>
                  <div className="sk-db__total">
                    <span>Total inc. VAT</span>
                    <strong>{formatGbp(quote.total)}</strong>
                  </div>
                </>
              ) : (
                <p className="sk-db__line" style={{ margin: 0 }}>
                  {quoteError ??
                    (valid
                      ? 'Working out your price…'
                      : !paintNamed
                        ? 'Name the colour to paint to see a price.'
                        : 'Check the sizes marked in the list to see a price.')}
                </p>
              )}
              {(doorBasket || savedDesigns.saves.has('doors')) && (
                <button type="button" className="sk-btn sk-btn--lg" disabled={!quote || busy} onClick={order}>
                  {doorBasket ? 'Add to basket' : 'Save to My designs'}
                </button>
              )}
              {status && (
                <p className="sk-db__status" role={status.kind === 'error' ? 'alert' : 'status'}>
                  {status.message}{' '}
                  {status.kind === 'ok' && !doorBasket && <SkLink href={myDesignsHref}>Open My designs</SkLink>}
                </p>
              )}
              <small>
                Every piece is made to order.
              </small>
            </div>
          </div>
        </div>
      </div>

      <div className="sk-db__popular">
        <div className="sk-db__wrap">
          <div className="sk-section-head" style={{ marginBottom: 0 }}>
            <p className="sk-eyebrow">Popular sizes</p>
            <h2 className="sk-heading sk-h2">{text(props.popularTitle, 'Standard UK door sizes')}</h2>
            <p className="sk-body">{text(props.popularIntro, 'Most UK kitchens use these sizes. Add one to your list and change anything you need.')}</p>
          </div>
          <ul className="sk-db__popgrid">
            {popular.map((size, i) => {
              const kind: PieceKind = size.h < 300 ? 'drawer' : 'door';
              return (
                <li key={i}>
                  <button
                    type="button"
                    className="sk-db__pop"
                    onClick={() => {
                      dispatch({ type: 'add', kind, h: size.h, w: size.w, side: kind === 'door' ? 'left' : 'none' });
                      document.getElementById('rows')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                  >
                    <strong>{`${size.h} × ${size.w} mm`}</strong>
                    <span>{text(size.label, PIECE_LABEL[kind])}</span>
                    <span className="sk-visually-hidden">Add to your list</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </BlockRoot>
  );
}
